/**
 * Le premier lancement.
 *
 * Deux questions, pas une de plus : à quel rythme l'argent rentre, et combien
 * il y a à gérer. Puis l'utilisateur est envoyé au budget pour répartir. Chaque
 * question posée avant celle-là est une occasion d'abandonner, sur un public
 * peu habitué aux applications.
 *
 * LA NUANCE QUI COMPTE : le montant saisi devient un vrai revenu, parce que la
 * question porte sur l'argent DISPONIBLE MAINTENANT — vérifiable en comptant
 * ses billets. L'estimation, elle, sert au cycle SUIVANT : elle est mémorisée
 * et reproposée, jamais transformée en écriture sans confirmation. Sans cette
 * séparation, « En poche » deviendrait une prévision, et l'app perdrait le seul
 * chiffre que l'utilisateur peut contrôler à la main.
 */
import { db, type Cycle } from './database'
import { applicableBudget, setEnvelope } from './budget'
import {
  clearRedo, cycleIncomeId, forgetCycleIncome, markRedo, pendingRedo,
  rememberCycleIncome, type RedoMode,
} from './marks'
import { deleteLocal, writeLocal } from './sync'
import { contains, cycleIncomeLabel, cycleRange, type CycleRange } from '../lib/cycles'
import { today } from '../lib/dates'
import { newId } from '../lib/uuid'

/**
 * L'écran est-il une REPRISE plutôt qu'un premier lancement ?
 *
 * Souhaiter la bienvenue à quelqu'un qui utilise l'app depuis des semaines
 * sonne faux, exactement comme à la clôture de cycle : il ne découvre pas
 * l'app, il corrige un réglage.
 */
export async function isRedoingOnboarding(walletId: string): Promise<RedoMode | null> {
  return pendingRedo(walletId)
}

/** Premier lancement : aucun rythme choisi, et pas la moindre écriture. */
export async function needsOnboarding(walletId: string): Promise<boolean> {
  const wallet = await db.wallets.get(walletId)
  if (!wallet) return false
  // Redemandé explicitement : on ne passe pas par les tests ci-dessous, qui
  // supposent une base vierge.
  if (await pendingRedo(walletId)) return true
  // `?? null` et non `!== null` : un portefeuille créé AVANT que ce champ
  // existe ne le porte pas du tout, et vaut donc `undefined`. C'est le cas de
  // tout utilisateur qui met l'app à jour — le comparer à `null` seul le
  // priverait silencieusement de l'accueil. Constaté dans le navigateur le
  // 2026-08-23, sur une base créée la veille.
  if ((wallet.expected_income ?? null) !== null) return false
  return (await db.transactions.where({ wallet_id: walletId }).count()) === 0
}

export async function completeOnboarding(input: {
  walletId: string
  cycle: Cycle
  /** L'argent disponible maintenant. 0 = l'utilisateur préfère commencer à vide. */
  amount: number
  /** Le montant à reproposer au cycle suivant. 0 = pas d'estimation. */
  expected: number
  /**
   * Appelé juste après l'écriture du portefeuille — l'instant exact où
   * `needsOnboarding` bascule à `false` et où la feuille se ferme.
   *
   * Sans ce point d'accroche, l'appelant qui navigue seulement une fois toute
   * la fonction résolue attend EN PLUS l'écriture du revenu d'ouverture,
   * plus lente d'un cran. Un tap sur la barre de navigation pile dans cet
   * écart lisait encore l'ancienne route, se voyait ignoré par le garde-fou
   * anti-double-navigation du routeur, puis se faisait écraser par la
   * redirection vers le budget, arrivée en retard.
   */
  onCycleApplied?: () => void
}): Promise<void> {
  const wallet = await db.wallets.get(input.walletId)
  if (!wallet) return

  /*
   * C'EST ICI que la reprise s'applique, et pas au clic dans le Profil.
   *
   * Demander la reprise ne touche à rien : elle lève un simple drapeau. Tant
   * que l'utilisateur n'a pas validé ces deux questions, il peut refermer la
   * feuille et retrouver ses données intactes. L'inverse — effacer d'abord,
   * afficher ensuite — laisse un « Annuler » qui ne peut plus rien rendre, ce
   * qui est pire que pas de bouton du tout.
   */
  await applyRestart(input.walletId, wallet.cycle ?? 'monthly', input.cycle)
  await clearRedo(input.walletId)

  await writeLocal('wallets', {
    id: wallet.id,
    name: wallet.name,
    cycle: input.cycle,
    expected_income: input.expected,
    manual_category_order: wallet.manual_category_order ?? false,
  })
  input.onCycleApplied?.()

  if (input.amount <= 0) {
    await forgetCycleIncome(input.walletId)
    return
  }

  const incomeId = newId()
  await rememberCycleIncome(input.walletId, incomeId)
  await writeLocal('transactions', {
    id: incomeId,
    wallet_id: input.walletId,
    // Sans catégorie, et volontairement : ce n'est ni un salaire ni un « autre
    // revenu », c'est L'ARGENT DU CYCLE. Une version antérieure prenait la
    // première catégorie de revenu que Dexie renvoyait — c'est-à-dire au
    // hasard, l'ordre de Dexie suivant l'identifiant : la même saisie
    // s'affichait tantôt « Salaire », tantôt « Autre revenu ».
    category_id: null,
    entered_by_account_id: null,
    amount: input.amount,
    direction: 'income' as const,
    operation_date: today(),
    title: cycleIncomeLabel(input.cycle),
    note: null,
  })
}

/**
 * Demander à reposer les deux questions du premier lancement, avec deux portées.
 *
 * Le rythme et le montant de départ se choisissent au tout premier écran, avant
 * d'avoir rien vu de l'app : c'est le moment où l'on se trompe le plus, et il
 * faut pouvoir y revenir. Reste que « je me suis trompé » recouvre deux
 * situations très différentes, et une seule action pour les deux fait
 * forcément trop ou pas assez :
 *
 * - `'settings'` — **corriger le cadre**. Le rythme et l'argent d'ouverture
 *   sont redemandés, tout le reste est conservé : opérations, enveloppes,
 *   journées prévues. Seul le revenu d'ouverture est remplacé, sinon le nouveau
 *   montant s'ajouterait à l'ancien.
 * - `'cycle'` — **recommencer la période**. Tout ce qui a été saisi POUR LE
 *   CYCLE EN COURS s'en va, opérations confirmées comprises.
 *
 * Dans les deux cas, **les cycles écoulés ne bougent pas** : ce sont des
 * périodes closes, dont l'argent a réellement été reçu et dépensé. À ne pas
 * confondre avec « repartir de zéro » (développement uniquement), qui efface la
 * base entière.
 *
 * **Rien n'est effacé ici** : la demande lève un drapeau, et c'est
 * `completeOnboarding` qui l'applique. Tant que l'utilisateur n'a pas répondu,
 * `cancelRestart` annule sans laisser de trace.
 */
export async function restartOnboarding(
  walletId: string, mode: RedoMode = 'settings',
): Promise<void> {
  if (!(await db.wallets.get(walletId))) return
  await markRedo(walletId, mode)
}

/** Renoncer à la reprise. Aucune donnée n'ayant été touchée, il n'y a rien à rendre. */
export async function cancelRestart(walletId: string): Promise<void> {
  await clearRedo(walletId)
}

/** La reprise demandée, appliquée au moment où l'utilisateur valide ses réponses. */
async function applyRestart(
  walletId: string, ancien: Cycle, nouveau: Cycle,
): Promise<void> {
  const mode = await pendingRedo(walletId)
  if (!mode) return

  // Le cycle tel qu'il était AVANT la réponse : c'est la période que
  // l'utilisateur avait sous les yeux en demandant la reprise.
  const range = cycleRange(today(), ancien)

  if (mode === 'cycle') {
    await wipeCycle(walletId, range, cycleRange(today(), nouveau))
  } else {
    // Le seul retrait de cette portée : l'argent que l'app a inscrit à
    // l'ouverture du cycle. Ce que l'utilisateur a saisi lui-même reste.
    const previous = await cycleIncomeId(walletId)
    const transaction = previous ? await db.transactions.get(previous) : undefined
    if (transaction && !transaction.deleted_at) {
      await deleteLocal('transactions', transaction.id)
    }
  }
  await forgetCycleIncome(walletId)
}

/** Tout ce qui a été saisi pour un cycle : opérations, journées, enveloppes. */
async function wipeCycle(
  walletId: string, range: CycleRange, apres: CycleRange,
): Promise<void> {
  const transactions = await db.transactions.where({ wallet_id: walletId }).toArray()
  for (const transaction of transactions) {
    if (transaction.deleted_at) continue
    if (!contains(range, transaction.operation_date)) continue
    // Tombstone et non suppression : la ligne est peut-être déjà partie au
    // serveur, et seul un tombstone l'y fera disparaître.
    await deleteLocal('transactions', transaction.id)
  }

  const allocations = await db.budget_allocations.where({ wallet_id: walletId }).toArray()
  for (const allocation of allocations) {
    if (allocation.deleted_at) continue
    if (!contains(range, allocation.planned_date)) continue
    await deleteLocal('budget_allocations', allocation.id)
  }

  /*
   * Les enveloppes se remettent à ZÉRO, elles ne se suppriment pas.
   *
   * `budgets` est versionnée par `effective_from`, et l'enveloppe qui s'applique
   * est la plus récente parmi celles déjà en vigueur : supprimer celle du cycle
   * en cours ferait remonter celle du cycle PRÉCÉDENT — exactement les montants
   * dont l'utilisateur veut se défaire.
   *
   * Le zéro se pose sur les deux dates de début quand le rythme change : passer
   * d'une semaine à un mois recule le début du cycle, et un zéro daté du 24 août
   * ne couvrirait pas un mois qui commence le 1er.
   */
  const debuts = [...new Set([range.start, apres.start])]
  const budgets = (await db.budgets.where({ wallet_id: walletId }).toArray())
    .filter((budget) => !budget.deleted_at)
  for (const categoryId of new Set(budgets.map((b) => b.category_id))) {
    for (const cycleStart of debuts) {
      const applicable = applicableBudget(budgets, categoryId, cycleStart)
      if (!applicable || applicable.amount === 0) continue
      await setEnvelope({
        walletId, categoryId, amount: 0, cycleStart,
        // Remettre un MONTANT à plat n'est pas remettre un RÉGLAGE à plat : sans
        // ces deux-là, l'enveloppe repartirait en « au fil de l'eau » et une
        // épargne perdrait son cumul, sans que personne l'ait demandé.
        planMode: applicable.plan_mode,
        carriesOver: applicable.carries_over,
      })
    }
  }
}
